/**
 * Publieke uitleg: Rapportage-tab — calculatie-export vs offerte (Fase 5 — gebruikershulp).
 */
import { Link } from 'react-router-dom';
import { AnimatedSection } from '../components/AnimatedSection';
import { Breadcrumbs } from '../components/Breadcrumbs';
import { SeoMeta } from '../components/SeoMeta';

export function HelpRapportageExport() {
  return (
    <div className="animate-page-enter">
      <SeoMeta
        path="/help/export"
        title="Export en offerte uit je dossier"
        description="Uitleg tab Rapportage CalqTool™: calculatie-export (CSV, Excel, begroting-PDF) met kostenopbouw en regeldetail; aparte klantofferte met preview en print/PDF."
      />
      <AnimatedSection delay={0.05}>
        <Breadcrumbs items={[{ label: 'Home', href: '/' }, { label: 'Export & offerte' }]} />
        <h1 className="mb-4 text-2xl font-bold text-text">Export en offerte</h1>
        <p className="mb-4 max-w-2xl text-sm text-text-muted">
          Op tab <strong className="text-text">Rapportage</strong> (stap 4 van je dossier) zitten twee duidelijke
          sporen: <strong className="text-text">calculatie-export</strong> (werkbestanden: CSV, Excel-vriendelijke CSV,
          begroting-PDF) en <strong className="text-text">offerte</strong> voor je klant (preview, print, server-PDF).
          Beide gebruiken dezelfde posten en totalen — de presentatie verschilt. Voor de calculatie bepaal je zelf via{' '}
          <strong className="text-text">Exportinstellingen</strong> hoe uitgebreid het bestand is (kostenopbouw,
          regeldetail, per-deel, CO₂, voorbehoud).
        </p>
        <p className="mb-8 max-w-2xl rounded-lg border border-border/80 bg-surface px-3 py-2 text-xs text-text-muted">
          Handleiding (footer → Handleidingen). Algemene storingen:{' '}
          <Link to="/status" className="font-medium text-primary hover:underline">dienststatus</Link>,{' '}
          <Link to="/troubleshoot" className="font-medium text-primary hover:underline">problemen oplossen</Link>.
        </p>

        <h2 className="mb-3 text-lg font-semibold text-text">Wanneer zie je Rapportage?</h2>
        <ul className="mb-8 max-w-2xl list-disc space-y-2 pl-5 text-sm text-text-muted">
          <li>
            Eerst <strong className="text-text">Regels opslaan</strong> op Posten; daarna worden Resultaat en Rapportage
            actief.
          </li>
          <li>
            Als de berekening op Resultaat faalt, kun je op Rapportage niet exporteren — los de fout daar of bij je
            posten op.
          </li>
        </ul>

        <h2 className="mb-3 text-lg font-semibold text-text">Calculatie-export</h2>
        <p className="mb-4 max-w-2xl text-sm text-text-muted">
          Voor interne controle en verwerking in Excel: <strong className="text-text">CSV</strong>,{' '}
          <strong className="text-text">Excel</strong> (UTF-8 met BOM, als .csv gedownload) en{' '}
          <strong className="text-text">begroting-PDF</strong> met leesbare tabellen. Optioneel kun je een blok{' '}
          <strong className="text-text">kostenopbouw</strong> (materialen, arbeid, materieel, subtotaal, modelcorrectie,
          totaal) laten voorgaan en <strong className="text-text">uitgebreide regeldetail</strong> (soort, bouwdeel,
          systeem-id) inschakelen. Bestandsnamen beginnen met{' '}
          <code className="rounded bg-surface px-1 text-text">calculatie_</code>.
        </p>

        <h2 className="mb-3 text-lg font-semibold text-text">Offerte</h2>
        <ol className="mb-8 max-w-2xl list-decimal space-y-3 pl-5 text-sm text-text-muted">
          <li>
            <strong className="text-text">Offerte genereren</strong> — preview in de app (zelfde logica als je totalen).
          </li>
          <li>
            <strong className="text-text">Print / PDF (browser)</strong> — nette afdruk van die preview.
          </li>
          <li>
            <strong className="text-text">Offerte-PDF (server)</strong> — onder{' '}
            <strong className="text-text">Exportinstellingen</strong>; handig zonder eerst de preview te openen.
          </li>
        </ol>

        <h2 className="mb-3 text-lg font-semibold text-text">Exportinstellingen</h2>
        <p className="mb-8 max-w-2xl text-sm text-text-muted">
          Via <strong className="text-text">Exportinstellingen</strong> op Rapportage stel je in: BTW, modelcorrectie
          (als je klanttype dat toont), CSV-scheidingsteken, <strong className="text-text">kostenopbouw</strong>,{' '}
          <strong className="text-text">uitgebreide regeldetail</strong>, <strong className="text-text">CO₂</strong> in
          de samenvatting (indien beschikbaar), <strong className="text-text">juridisch voorbehoud</strong> (CSV, Excel
          en begroting-PDF), en <strong className="text-text">per-deel totalen</strong> in begroting-PDF. De regel{' '}
          <strong className="text-text">Actief:</strong> op het dossier vat je keuzes samen.
        </p>

        <div className="mb-8 max-w-2xl rounded-lg border border-amber-200/80 bg-amber-50/90 px-4 py-3 text-sm text-text-muted dark:border-amber-900/50 dark:bg-amber-950/30 dark:text-amber-100/90">
          <strong className="text-text">Model en voorbehoud:</strong> uitkomsten zijn indicatief. Zie de{' '}
          <Link to="/terms" className="font-medium text-primary hover:underline">
            algemene voorwaarden
          </Link>{' '}
          en de tekst op de Rapportage-tab zelf.
        </div>

        <div className="mb-8 flex flex-wrap gap-3">
          <Link
            to="/help/tekening"
            className="inline-flex rounded-lg border border-border bg-surface-card px-4 py-2 text-sm font-medium text-text hover:border-primary"
          >
            ← Tekening &amp; PDF
          </Link>
          <Link
            to="/troubleshoot"
            className="inline-flex rounded-lg border border-border bg-surface-card px-4 py-2 text-sm font-medium text-text hover:border-primary"
          >
            Problemen oplossen
          </Link>
        </div>

        <p className="text-xs text-text-muted">
          Technische checklist voor het team:{' '}
          <code className="rounded bg-surface px-1">docs/RAPPORTAGE_EXPORT_FLOW.md</code>
        </p>
      </AnimatedSection>
    </div>
  );
}
